import React, { useCallback, useMemo } from 'react';
import { IRowMeta, setStepPartition } from '../../../redux/slices/oldBuilder';
import PartitionValuesDisplay from './PartitionValuesDisplay';
import { IPartitionValueFieldsFragment } from '../../../utils/apollo/graphqlSchema';
import ConversionWindowInput from './ConversionWindowInput';
import { dropdownStyle } from '../../Dropdown/ContextualMenu/KubitContextualMenu';
import KubitContextualMenu from '../../Dropdown/ContextualMenu';
import { IContextualMenuItem } from '@fluentui/react';
import ContextualMenuSubItemWrapper from '../../Dropdown/ContextualMenu/ContextualMenuSubItemWrapper';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { currentStepSelector } from '../../../redux/selectors/builder';
import { stepPartitionOptions } from '../../../redux/slices/oldBuilder/defaults';
import { SingleSelectLabel } from '../../Dropdown/SingleSelect';
import ContextualMenuOption from '../../Dropdown/ContextualMenu/ContextualMenuOption';

interface IProps {
  rowMeta: IRowMeta;
}

export default ({ rowMeta }: IProps) => {
  const dispatch = useAppDispatch();

  const { rowIndex } = rowMeta;

  const currentStep = useAppSelector((state) =>
    currentStepSelector(state, rowIndex),
  );

  const { partitionValue } = currentStep;

  const setPartitionValue = useCallback(
    (value: IPartitionValueFieldsFragment) => {
      dispatch(
        setStepPartition({
          stepIndex: rowIndex,
          value,
        }),
      );
    },
    [dispatch, rowIndex],
  );

  const items = useMemo<IContextualMenuItem[]>(
    () =>
      stepPartitionOptions.reduce<IContextualMenuItem[]>(
        (result, partition) => {
          if (partition.__typename === 'NonePartition') {
            result.unshift({
              key: partition.key,
              onRenderContent: () => (
                <ContextualMenuOption
                  label={partition.name}
                  onClick={() => {
                    setPartitionValue({
                      __typename: 'PartitionValue',
                      partition,
                      conversionWindow: null,
                    });
                  }}
                />
              ),
            });
          }

          if (partition.__typename === 'ConversionWindowPartition') {
            result.push({
              key: partition.key,
              onRenderContent: () => (
                <ContextualMenuOption
                  label={partition.name}
                  hasSubMenus={true}
                />
              ),
              subMenuProps: {
                styles: dropdownStyle,
                items: [
                  {
                    key: 'conversion-window-input',
                    onRender: (_, dismissSubMenu) => (
                      <ContextualMenuSubItemWrapper>
                        <ConversionWindowInput
                          conversionWindow={partitionValue?.conversionWindow}
                          setConversionWindow={(duration) =>
                            setPartitionValue({
                              __typename: 'PartitionValue',
                              partition,
                              conversionWindow: duration,
                            })
                          }
                          // only close the submenu when click Apply
                          onApply={() => dismissSubMenu(undefined, true)}
                          isDayPartitionSelected={false}
                          isTimeToConvertMode={false}
                        />
                      </ContextualMenuSubItemWrapper>
                    ),
                  },
                ],
              },
            });
          }

          return result;
        },
        [],
      ),
    [partitionValue, setPartitionValue],
  );

  return (
    <KubitContextualMenu
      items={items}
      trigger={
        <SingleSelectLabel isAlwaysActive={true}>
          <PartitionValuesDisplay
            partitionValues={partitionValue ? [partitionValue] : []}
          />
        </SingleSelectLabel>
      }
      style={{
        root: {
          minWidth: '160px',
        },
      }}
    />
  );
};
